/**
 * Sandbox KYC panel — PAN verification + DigiLocker OKYC.
 * Drops into both StartupKYC and InvestorKYC pages.
 *
 * Product rules (locked in with user):
 *   • PAN + name + DOB → single API call, pass/fail returned immediately.
 *   • DigiLocker is a redirect flow. We store session_id in sessionStorage
 *     so the /kyc/digilocker/callback page can call /complete.
 *   • Successful DigiLocker locks profile fields until admin override.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, IdCard, ExternalLink, CheckCircle2, XCircle, Lock, Building2, Landmark, FileBadge } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button, Card, Pill } from "../common/Primitives";

const DIGILOCKER_CONSENT_TEXT =
  "I authorise VenturaZ (Ultiwise Ventures Private Limited) to fetch my Aadhaar KYC data " +
  "(name, date of birth, gender, address) from DigiLocker via Sandbox APIs, solely for " +
  "identity verification on this platform, in accordance with the DPDPA, 2023.";

function StatusPill({ status, locked }) {
  if (!status) return <Pill tone="muted" data-testid="sandbox-status-none">Not started</Pill>;
  const s = status.toLowerCase();
  if (s === "succeeded") return <Pill tone="success" data-testid="sandbox-status-ok">Verified{locked ? " · Locked" : ""}</Pill>;
  if (s === "pending") return <Pill tone="warn" data-testid="sandbox-status-pending">Pending</Pill>;
  if (s === "failed" || s === "expired" || s === "rejected") return <Pill tone="danger" data-testid="sandbox-status-fail">{status}</Pill>;
  return <Pill tone="muted" data-testid="sandbox-status-other">{status}</Pill>;
}

export default function SandboxKycPanel({ showCompany = false, showIndividual = true } = {}) {
  const [state, setState] = useState({ loading: true, sandbox_enabled: false, pan: null, digilocker: null, identity_verified: false, company_verified: false, checks: {} });
  const [panForm, setPanForm] = useState({ pan: "", name_as_per_pan: "", date_of_birth: "" });
  const [panBusy, setPanBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [consented, setConsented] = useState(false);
  // Company checks
  const [cpanForm, setCpanForm] = useState({ pan: "", company_name: "", incorporation_date: "" });
  const [cinInput, setCinInput] = useState("");
  const [gstinInput, setGstinInput] = useState("");
  const [companyBusy, setCompanyBusy] = useState({ pan: false, cin: false, gstin: false });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/kyc/sandbox/status");
      setState({ loading: false, ...data });
    } catch (err) {
      setState((s) => ({ ...s, loading: false }));
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitPan = async (e) => {
    e.preventDefault();
    if (!panForm.pan.trim() || !panForm.name_as_per_pan.trim() || !panForm.date_of_birth.trim()) {
      toast.error("PAN, name and date of birth are required.");
      return;
    }
    setPanBusy(true);
    try {
      const { data } = await api.post("/kyc/sandbox/pan/verify", {
        pan: panForm.pan.trim().toUpperCase(),
        name_as_per_pan: panForm.name_as_per_pan.trim(),
        date_of_birth: panForm.date_of_birth.trim(),
      });
      if (data.ok) toast.success(`PAN verified · ${data.pan_masked}`);
      else toast.error(`PAN check failed · name-match=${data.name_match}, dob-match=${data.dob_match}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPanBusy(false);
    }
  };

  const startDigilocker = async () => {
    if (!consented) {
      toast.error("Please tick the consent checkbox before starting DigiLocker.");
      return;
    }
    setDlBusy(true);
    try {
      const { data } = await api.post("/kyc/sandbox/digilocker/init", {
        consent_text: DIGILOCKER_CONSENT_TEXT,
      });
      // Persist session_id for the callback page
      sessionStorage.setItem("venturaz_digilocker_session_id", data.session_id);
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(formatApiError(err));
      setDlBusy(false);
    }
  };

  if (state.loading) {
    return <Card testId="sandbox-kyc-loading" className="max-w-2xl"><div className="text-ink-faint text-sm">Loading identity checks…</div></Card>;
  }

  if (!state.sandbox_enabled) {
    return (
      <Card testId="sandbox-kyc-disabled" className="max-w-2xl">
        <div className="text-ink-soft text-sm">
          Instant identity verification is temporarily unavailable. Please upload
          documents in the document vault below — our team will verify manually.
        </div>
      </Card>
    );
  }

  const digilockerDone = state.digilocker?.status === "succeeded";
  const digilockerLocked = digilockerDone && state.digilocker?.locked;

  // ---------------- Company check handlers ----------------
  const runCompanyPan = async (e) => {
    e.preventDefault();
    if (!cpanForm.pan || !cpanForm.company_name || !cpanForm.incorporation_date) {
      toast.error("Company PAN, legal name and date of incorporation are all required.");
      return;
    }
    setCompanyBusy((b) => ({ ...b, pan: true }));
    try {
      const { data } = await api.post("/kyc/sandbox/company/pan", {
        pan: cpanForm.pan.trim().toUpperCase(),
        company_name: cpanForm.company_name.trim(),
        incorporation_date: cpanForm.incorporation_date.trim(),
      });
      if (data.ok) toast.success(`Company PAN verified · ${data.pan_masked}`);
      else toast.error(`Company PAN check failed (name-match=${data.name_match ?? "-"})`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCompanyBusy((b) => ({ ...b, pan: false })); }
  };

  const runCin = async () => {
    if (!cinInput.trim()) { toast.error("Enter your CIN."); return; }
    setCompanyBusy((b) => ({ ...b, cin: true }));
    try {
      const { data } = await api.post("/kyc/sandbox/cin", { cin: cinInput.trim().toUpperCase() });
      if (data.ok) toast.success(`CIN verified · ${data.parsed?.company_name || cinInput}`);
      else toast.error("CIN not found on MCA");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCompanyBusy((b) => ({ ...b, cin: false })); }
  };

  const runGstin = async () => {
    if (!gstinInput.trim()) { toast.error("Enter your GSTIN."); return; }
    setCompanyBusy((b) => ({ ...b, gstin: true }));
    try {
      const { data } = await api.post("/kyc/sandbox/gstin", { gstin: gstinInput.trim().toUpperCase() });
      if (data.ok) toast.success(`GSTIN verified · ${data.parsed?.legal_name || gstinInput}`);
      else toast.error("GSTIN invalid or inactive");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCompanyBusy((b) => ({ ...b, gstin: false })); }
  };

  const cpanRec = state.checks?.company_pan;
  const cinRec = state.checks?.cin;
  const gstinRec = state.checks?.gstin;

  return (
    <div className="space-y-6">
      {/* ---------------- Aggregate banner ---------------- */}
      {showIndividual && state.identity_verified && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3" data-testid="identity-verified-banner">
          <CheckCircle2 className="text-emerald-600" size={20} />
          <div className="flex-1">
            <div className="font-heading text-emerald-800 text-sm">Founder identity verified</div>
            <div className="text-xs text-emerald-700/80">Aadhaar (DigiLocker) + PAN both confirmed. Name and DOB are locked — contact an admin to unlock if you need corrections.</div>
          </div>
          <Lock size={16} className="text-emerald-700" />
        </div>
      )}
      {showCompany && state.company_verified && (
        <div className="rounded-lg border border-navy/20 bg-navy/[0.04] px-4 py-3 flex items-center gap-3" data-testid="company-verified-banner">
          <CheckCircle2 className="text-navy" size={20} />
          <div className="flex-1">
            <div className="font-heading text-ink text-sm">Company entity verified</div>
            <div className="text-xs text-ink-soft">Company PAN + CIN both confirmed against MCA and Income Tax records.</div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- DigiLocker */}
      {showIndividual && (
      <Card testId="sandbox-digilocker-card" className="max-w-2xl">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck className="text-gold" size={22} />
          <div className="flex-1">
            <div className="font-heading text-ink text-lg">Aadhaar via DigiLocker</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">
              Government-issued · Instant
            </div>
          </div>
          <StatusPill status={state.digilocker?.status} locked={digilockerLocked} />
        </div>

        {digilockerLocked ? (
          <div className="rounded-lg border border-ink-line/30 bg-ink-line/5 p-4 text-sm space-y-1" data-testid="sandbox-digilocker-summary">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 size={16} /> Verified via DigiLocker · <span className="font-mono text-xs text-ink-faint">{state.digilocker.verification_id?.slice(0, 8)}</span>
            </div>
            {state.digilocker.parsed?.name && <div><span className="text-ink-muted">Name:</span> {state.digilocker.parsed.name}</div>}
            {state.digilocker.parsed?.dob && <div><span className="text-ink-muted">DOB:</span> {state.digilocker.parsed.dob}</div>}
            {state.digilocker.parsed?.gender && <div><span className="text-ink-muted">Gender:</span> {state.digilocker.parsed.gender}</div>}
            {state.digilocker.parsed?.address && <div className="line-clamp-2"><span className="text-ink-muted">Address:</span> {state.digilocker.parsed.address}</div>}
            <div className="mt-2 text-ink-faint text-xs flex items-center gap-1"><Lock size={12} /> Your identity fields are locked. Admin can unlock if a correction is needed.</div>
          </div>
        ) : (
          <>
            <p className="text-ink-soft text-sm leading-relaxed">
              You will be redirected to DigiLocker to sign in with Aadhaar OTP and grant consent.
              VenturaZ receives only your <strong>name, date of birth, gender and address</strong> —
              your photo is never fetched or stored.
            </p>
            <label className="mt-4 flex items-start gap-2 text-xs text-ink-soft cursor-pointer" data-testid="digilocker-consent">
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                className="mt-0.5"
                data-testid="digilocker-consent-checkbox"
              />
              <span>{DIGILOCKER_CONSENT_TEXT}</span>
            </label>
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={startDigilocker}
                disabled={!consented || dlBusy}
                data-testid="start-digilocker-btn"
              >
                {dlBusy ? "Redirecting…" : <span className="inline-flex items-center gap-1.5">Verify via DigiLocker <ExternalLink size={14} /></span>}
              </Button>
              {state.digilocker?.status === "pending" && (
                <span className="text-xs text-ink-faint">A previous session is pending — starting a new one will replace it.</span>
              )}
            </div>
          </>
        )}
      </Card>
      )}

      {/* --------------------------------------------------------------- PAN */}
      {showIndividual && (
      <Card testId="sandbox-pan-card" className="max-w-2xl">
        <div className="flex items-center gap-3 mb-3">
          <IdCard className="text-gold" size={22} />
          <div className="flex-1">
            <div className="font-heading text-ink text-lg">PAN verification</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">
              Name-match against Income Tax records
            </div>
          </div>
          <StatusPill status={state.pan?.status} locked={state.pan?.locked} />
        </div>

        {state.pan?.status === "succeeded" ? (
          <div className="rounded-lg border border-ink-line/30 bg-ink-line/5 p-3 text-sm" data-testid="sandbox-pan-summary">
            <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={16} /> PAN verified · {state.pan.request_ref_masked}</div>
            <div className="text-xs text-ink-faint mt-1">Aadhaar seeding: {state.pan.parsed?.aadhaar_seeding || "—"}</div>
          </div>
        ) : (
          <form onSubmit={submitPan} className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="sandbox-pan-form">
            <input
              value={panForm.pan}
              onChange={(e) => setPanForm({ ...panForm, pan: e.target.value.toUpperCase() })}
              placeholder="ABCDE1234F"
              maxLength={10}
              className="px-3 py-2 rounded border border-ink-line/40 bg-white text-sm uppercase tracking-widest"
              data-testid="pan-input"
            />
            <input
              value={panForm.name_as_per_pan}
              onChange={(e) => setPanForm({ ...panForm, name_as_per_pan: e.target.value })}
              placeholder="Name as on PAN"
              className="px-3 py-2 rounded border border-ink-line/40 bg-white text-sm"
              data-testid="pan-name-input"
            />
            <input
              value={panForm.date_of_birth}
              onChange={(e) => setPanForm({ ...panForm, date_of_birth: e.target.value })}
              placeholder="DD/MM/YYYY"
              className="px-3 py-2 rounded border border-ink-line/40 bg-white text-sm"
              data-testid="pan-dob-input"
            />
            <div className="sm:col-span-3">
              <Button type="submit" disabled={panBusy} data-testid="verify-pan-btn">
                {panBusy ? "Verifying…" : "Verify PAN"}
              </Button>
              {state.pan?.status === "failed" && (
                <span className="ml-3 text-xs text-red-600 inline-flex items-center gap-1">
                  <XCircle size={12} /> Previous attempt failed — try again with matching name.
                </span>
              )}
            </div>
          </form>
        )}
      </Card>
      )}

      {/* ------------------------------------------- Company entity checks */}
      {showCompany && (
        <>
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted pt-2">
            Company entity · Powered by MCA & GST portal
          </div>

          {/* Company PAN */}
          <Card testId="sandbox-company-pan-card" className="max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              <Building2 className="text-gold" size={20} />
              <div className="flex-1">
                <div className="font-heading text-ink text-lg">Company PAN</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">Legal-name match against Income Tax</div>
              </div>
              <StatusPill status={cpanRec?.status} locked={cpanRec?.locked} />
            </div>
            {cpanRec?.status === "succeeded" ? (
              <div className="rounded-lg border border-ink-line/30 bg-ink-line/5 p-3 text-sm" data-testid="sandbox-company-pan-summary">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={16} /> {cpanRec.request_ref_masked} · category={cpanRec.parsed?.category}</div>
              </div>
            ) : (
              <form onSubmit={runCompanyPan} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input value={cpanForm.pan} onChange={(e) => setCpanForm({ ...cpanForm, pan: e.target.value.toUpperCase() })} placeholder="AAACU5052B" maxLength={10} className="px-3 py-2 rounded border border-ink-line/40 bg-white text-sm uppercase tracking-widest" data-testid="cpan-input" />
                <input value={cpanForm.company_name} onChange={(e) => setCpanForm({ ...cpanForm, company_name: e.target.value })} placeholder="Company legal name" className="px-3 py-2 rounded border border-ink-line/40 bg-white text-sm" data-testid="cpan-name-input" />
                <input value={cpanForm.incorporation_date} onChange={(e) => setCpanForm({ ...cpanForm, incorporation_date: e.target.value })} placeholder="DD/MM/YYYY (incorporation)" className="px-3 py-2 rounded border border-ink-line/40 bg-white text-sm" data-testid="cpan-dob-input" />
                <div className="sm:col-span-3">
                  <Button type="submit" disabled={companyBusy.pan} data-testid="verify-company-pan-btn">
                    {companyBusy.pan ? "Verifying…" : "Verify Company PAN"}
                  </Button>
                </div>
              </form>
            )}
          </Card>

          {/* CIN */}
          <Card testId="sandbox-cin-card" className="max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              <Landmark className="text-gold" size={20} />
              <div className="flex-1">
                <div className="font-heading text-ink text-lg">CIN — MCA master data</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">Pulls registered company details from MCA</div>
              </div>
              <StatusPill status={cinRec?.status} locked={cinRec?.locked} />
            </div>
            {cinRec?.status === "succeeded" ? (
              <div className="rounded-lg border border-ink-line/30 bg-ink-line/5 p-3 text-sm space-y-0.5" data-testid="sandbox-cin-summary">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={16} /> {cinRec.parsed?.company_name}</div>
                <div className="text-xs text-ink-soft">Status: {cinRec.parsed?.status} · ROC: {cinRec.parsed?.roc_code}</div>
                <div className="text-xs text-ink-faint line-clamp-2">{cinRec.parsed?.registered_address}</div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <input value={cinInput} onChange={(e) => setCinInput(e.target.value.toUpperCase())} placeholder="U74140UP2019PTC122523" className="flex-1 px-3 py-2 rounded border border-ink-line/40 bg-white text-sm uppercase tracking-widest" data-testid="cin-input" />
                <Button onClick={runCin} disabled={companyBusy.cin} data-testid="verify-cin-btn">
                  {companyBusy.cin ? "Looking up…" : "Verify CIN"}
                </Button>
              </div>
            )}
          </Card>

          {/* GSTIN */}
          <Card testId="sandbox-gstin-card" className="max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              <FileBadge className="text-gold" size={20} />
              <div className="flex-1">
                <div className="font-heading text-ink text-lg">GSTIN</div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">Active status & legal name via GST portal</div>
              </div>
              <StatusPill status={gstinRec?.status} locked={gstinRec?.locked} />
            </div>
            {gstinRec?.status === "succeeded" ? (
              <div className="rounded-lg border border-ink-line/30 bg-ink-line/5 p-3 text-sm space-y-0.5" data-testid="sandbox-gstin-summary">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={16} /> {gstinRec.parsed?.legal_name}</div>
                <div className="text-xs text-ink-soft">Status: {gstinRec.parsed?.status} · State: {gstinRec.parsed?.state}</div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <input value={gstinInput} onChange={(e) => setGstinInput(e.target.value.toUpperCase())} placeholder="09AACCU5052B1ZM" maxLength={15} className="flex-1 px-3 py-2 rounded border border-ink-line/40 bg-white text-sm uppercase tracking-widest" data-testid="gstin-input" />
                <Button onClick={runGstin} disabled={companyBusy.gstin} data-testid="verify-gstin-btn">
                  {companyBusy.gstin ? "Verifying…" : "Verify GSTIN"}
                </Button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
